import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
} from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import BalanceIcon from "@mui/icons-material/Balance";
import HighQualityIcon from "@mui/icons-material/HighQuality";

export type CropQualityOption = "fast" | "balanced" | "highQuality";

interface CropOptionsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (option: CropQualityOption) => void;
}

const CropOptionsDialog: React.FC<CropOptionsDialogProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [selectedOption, setSelectedOption] =
    React.useState<CropQualityOption>("balanced");

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value as CropQualityOption);
  };

  const handleConfirm = () => {
    onConfirm(selectedOption);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" component="div">
          Cropping Performance Options
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Cropping videos requires re-encoding, which can be a slow process
          depending on your device performance and video file size.
        </Alert>

        <Typography variant="body2" gutterBottom>
          Select a processing option that balances speed and quality for your
          needs:
        </Typography>

        <RadioGroup
          value={selectedOption}
          onChange={handleOptionChange}
          sx={{ mt: 2 }}
        >
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              border: "1px solid #eee",
              borderRadius: 1,
              "&:hover": { bgcolor: "#f9f9f9" },
            }}
          >
            <FormControlLabel
              value="fast"
              control={<Radio />}
              label={
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BoltIcon color="success" />
                    <Typography variant="subtitle1" fontWeight={500}>
                      Fast
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Prioritizes processing speed over quality. Best for quick
                    edits and previews.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box
            sx={{
              mb: 2,
              p: 1.5,
              border: "1px solid #eee",
              borderRadius: 1,
              "&:hover": { bgcolor: "#f9f9f9" },
            }}
          >
            <FormControlLabel
              value="balanced"
              control={<Radio />}
              label={
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BalanceIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={500}>
                      Balanced
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Moderate processing speed with good quality. Recommended for
                    most uses.
                  </Typography>
                </Box>
              }
            />
          </Box>

          <Box
            sx={{
              p: 1.5,
              border: "1px solid #eee",
              borderRadius: 1,
              "&:hover": { bgcolor: "#f9f9f9" },
            }}
          >
            <FormControlLabel
              value="highQuality"
              control={<Radio />}
              label={
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <HighQualityIcon color="error" />
                    <Typography variant="subtitle1" fontWeight={500}>
                      High Quality
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Best possible quality, but slower processing. Suitable for
                    final exports.
                  </Typography>
                </Box>
              }
            />
          </Box>
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleConfirm} variant="contained">
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CropOptionsDialog;
